import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useVehicles } from '../../../hooks/useVehicles'
import { VehicleIdentityCard, getVehicleDisplayName } from '@/components/vehicles/VehicleIdentityCard'
import { type VehicleIdentityData } from '@/components/vehicles/vehicleIdentity'
import { VehicleSearchInput } from '@/components/vehicles/VehicleSearchInput'

interface VehicleSearchFieldProps {
 value: string
 onChange: (vehicleId: string) => void
 placeholder?: string
 disabled?: boolean
}

export const VehicleSearchField: React.FC<VehicleSearchFieldProps> = ({
 value,
 onChange,
 placeholder,
 disabled = false
}) => {
 const { t } = useTranslation()
 const effectivePlaceholder = placeholder || t('proposals.form.searchVehicle')
 const { vehicles, isLoading, fetchVehicles } = useVehicles()
 const [searchTerm, setSearchTerm] = useState('')
 const [showResults, setShowResults] = useState(false)
 const [selectedVehicle, setSelectedVehicle] = useState<VehicleIdentityData | null>(null)
 const [vehiclesLoaded, setVehiclesLoaded] = useState(false)

 useEffect(() => {
 if (searchTerm.length >= 2) {
 const timer = setTimeout(() => {
 fetchVehicles({ search: searchTerm, limit: 10 })
 setShowResults(true)
 }, 300)
 return () => clearTimeout(timer)
 } else if (searchTerm.length === 0) {
 setShowResults(false)
 }
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [searchTerm])

 useEffect(() => {
 if (value && !vehiclesLoaded && vehicles.length === 0) {
 fetchVehicles({ limit: 100 })
 setVehiclesLoaded(true)
 }
 }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

 useEffect(() => {
 if (value && vehicles.length > 0) {
 const vehicle = vehicles.find((v) => v.id === value)
 if (vehicle) {
 setSelectedVehicle(vehicle as VehicleIdentityData)
 }
 }
 }, [value, vehicles])

 const handleVehicleSelect = (vehicle: VehicleIdentityData) => {
 setSelectedVehicle(vehicle)
 onChange(vehicle.id)
 setSearchTerm('')
 setShowResults(false)
 }

 const handleClear = () => {
 setSelectedVehicle(null)
 onChange('')
 setSearchTerm('')
 setShowResults(false)
 }

 const handleInputFocus = () => {
 if (searchTerm.length >= 2) {
 setShowResults(true)
 }
 }

 return (
 <div className="relative">
 <VehicleSearchInput
 value={selectedVehicle ? getVehicleDisplayName(selectedVehicle) : searchTerm}
 onChange={(nextValue) => {
 if (selectedVehicle) {
 handleClear()
 return
 }
 setSearchTerm(nextValue)
 }}
 onFocus={handleInputFocus}
 placeholder={effectivePlaceholder}
 disabled={disabled}
 showClear={Boolean(selectedVehicle)}
 onClear={handleClear}
 />

 {showResults && searchTerm.length >= 2 && (
 <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
 {isLoading ? (
 <div className="p-3 text-center text-sm text-muted-foreground">
 {t('common.loading')}...
 </div>
 ) : vehicles.length > 0 ? (
 <div className="py-1">
 {vehicles.map((vehicle) => (
 <button
 key={vehicle.id}
 type="button"
 onClick={() => handleVehicleSelect(vehicle as VehicleIdentityData)}
 className="w-full px-4 py-3 text-left hover:bg-surface focus:bg-surface dark:focus:bg-gray-700 focus:outline-none"
 >
 <VehicleIdentityCard vehicle={vehicle as VehicleIdentityData} />
 </button>
 ))}
 </div>
 ) : (
 <div className="p-3 text-center text-sm text-muted-foreground">
 {t('common.noResults')}
 </div>
 )}
 </div>
 )}
 </div>
 )
}
